import {
  EXTENSIONS_SELF_REPORTED,
  EXTENSION_URL,
  FAMILY_HISTORY_MATERNAL_PATERNAL,
} from '@constants/extension-codes';
import { FAMILY_HISTORY_RELATIONSHIP_OPTIONS } from '@constants/option-types/family-history';

import {
  FamilyHistoryRecord,
  resolveDeceasedFields,
  resolveDiagnosedFields,
  type FamilyHistoryValues,
} from '@models/family-history';

import { capitalize, formatPatientId } from '@utils/format';

import type { Extension, FamilyMemberHistoryModelInput } from 'generated/types';
import { FamilyMemberHistoryStatus } from 'generated/types';

export function getFamilyHistoryTemplate(
  patient_id: string,
  values: FamilyHistoryValues
): FamilyMemberHistoryModelInput {
  const relationship = FAMILY_HISTORY_RELATIONSHIP_OPTIONS.find(
    ({ code }) => code === values.relationshipCode
  );

  const extensionFields: Extension[] = [];
  if (
    relationship?.category === 'maternal' ||
    relationship?.category === 'paternal' ||
    relationship?.category === 'nuclear'
  ) {
    extensionFields.push({
      url: `${EXTENSION_URL}${FAMILY_HISTORY_MATERNAL_PATERNAL}`,
      valueString: capitalize(relationship.category),
    });
  }
  if (values.associatedResources) {
    values.associatedResources.forEach((resource) => {
      extensionFields.push({
        url: 'https://vitall.com/documentation/extensions/associated-records',
        valueReference: {
          reference: resource,
        },
      });
    });
  }
  if (values.selfReported) {
    extensionFields.push({
      url: `${EXTENSION_URL}${EXTENSIONS_SELF_REPORTED}`,
      valueBoolean: values.selfReported,
    });
  }

  const deceasedFields = resolveDeceasedFields(
    values.isDeceased,
    values.deceasedDate
  );

  return {
    resourceType: 'FamilyMemberHistory',
    status: FamilyMemberHistoryStatus.completed,
    patient: {
      reference: formatPatientId(patient_id),
    },
    relationship: {
      coding: [
        {
          code: relationship?.code ?? 'other',
          display:
            relationship?.display ?? capitalize(values.relationshipName ?? ''),
          system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
        },
      ],
      text: relationship?.text ?? capitalize(values.relationshipName ?? ''),
    },
    ...(values.dateOfBirth && {
      bornDate: values.dateOfBirth,
    }),
    // Create path: every condition is new, so there is no stored condition to
    // preserve and no note[1...] to carry - Age Diagnosed simply becomes
    // note[0] and Date Diagnosed onsetPeriod.start.
    condition: values.conditions.map(
      ({ ageDiagnosed, diagnosedYear, name }) => ({
        code: {
          text: name,
          coding: [
            {
              display: name,
              system: FamilyHistoryRecord.VITALL_SYSTEM,
            },
          ],
        },
        ...resolveDiagnosedFields(ageDiagnosed, diagnosedYear),
      })
    ),
    ...deceasedFields,
    extension: extensionFields,
    ...(values.description && {
      note: [
        {
          text: values.description,
        },
      ],
    }),
  };
}
